import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import {
  MapPin, Plus, Search, Building2, Trash2, Edit, X, Save,
  Phone, Mail, Globe, ExternalLink, Loader2, Upload, Image,
  ChevronDown, Check, AlertCircle, Star, Copy, Heart, Clock,
  User, Briefcase, Users, ShoppingBag, FileText, Facebook, Twitter, Youtube,
  CreditCard, Scale, Lock, Unlock
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getAllPlaces, createPlace, updatePlace, deletePlace, linkPlaceToPeople, getPlaceRelation } from '../../services/placesService';
import { getUser } from '../../services/authService';
import { getPlacesTypes, getPlacesTypesGrouped, categoryLabels } from '../../services/placesTypesService';
import { searchByVatNumber, searchByName, getPlaceTypeFromNace } from '../../services/cbeService';
import { uploadFile } from '../../services/filesService';
import AddressAutocomplete from '../AddressAutocomplete';
import ImageUpload from '../ImageUpload';
import CustomDropdown from '../ui/CustomDropdown';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';
import { countries, searchCountries, getPhonePrefix, getCountryByCode } from '../../utils/countries';
import { validatePlaceForm, hasErrors, isValidEmail, isValidPhone, isValidUrl, ensureUrlProtocol, normalizePhoneNumber } from '../../utils/validators';

const EndroitsPage = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const lang = language?.substring(0, 2) || 'fr';
  const user = getUser();

  // State
  const [places, setPlaces] = useState([]);
  const [placesTypes, setPlacesTypes] = useState([]);
  const [groupedTypes, setGroupedTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null); // Category filter
  const [selectedTypes, setSelectedTypes] = useState([]); // Types within category
  const [formCategory, setFormCategory] = useState(''); // Category in form
  const [showForm, setShowForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showGoogleUrlAlert, setShowGoogleUrlAlert] = useState(false);

  // BCE Search state
  const [selectedCountry, setSelectedCountry] = useState('BE');
  const [bceMode, setBceMode] = useState('vat'); // 'vat' or 'name'
  const [searchVat, setSearchVat] = useState('');
  const [searchName, setSearchName] = useState('');
  const [bceSearching, setBceSearching] = useState(false);
  const [bceError, setBceError] = useState('');
  const [bceResult, setBceResult] = useState(null);
  const [bceNameResults, setBceNameResults] = useState([]);
  const [bceResultsHidden, setBceResultsHidden] = useState(false); // To hide/show results

  // Google Places autocomplete state
  const [placesSuggestions, setPlacesSuggestions] = useState([]);
  const [placesSearching, setPlacesSearching] = useState(false);
  const [showPlacesSuggestions, setShowPlacesSuggestions] = useState(false);
  const placesAutocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Relationship types
  const RELATIONSHIP_TYPES = [
    { value: 'owner', label: 'Patron / Gérant', icon: 'Briefcase' },
    { value: 'employee', label: 'Employé', icon: 'User' },
    { value: 'worker', label: 'Ouvrier', icon: 'Users' },
    { value: 'client', label: 'Client', icon: 'ShoppingBag' },
    { value: 'visitor', label: 'Visiteur', icon: 'MapPin' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    placeName: '',
    subName: '',
    placeTypeId: '',
    placeType: '',
    googleAddress: '',
    latitude: null,
    longitude: null,
    email: '',
    phone: '',
    website: '',
    contactName: '',
    description: '',
    tvaNum: '',
    bankNum: '',
    firmType: '',
    facebookUrl: '',
    twitterUrl: '',
    youtubeUrl: '',
    googleUrl: '', // URL vers la page Google de l'établissement
    parking: false,
    status: true,
    relationshipType: '', // Relation avec l'établissement
    addToFavorites: false // Ajouter aux favoris
  });

  // Google Places selected result
  const [googlePlaceResult, setGooglePlaceResult] = useState(null);
  const [googlePlacePhotos, setGooglePlacePhotos] = useState([]);
  const [copiedFields, setCopiedFields] = useState({}); // Track which fields have been copied

  // Form validation and helpers
  const [formErrors, setFormErrors] = useState({});
  const [websiteProtocol, setWebsiteProtocol] = useState('https://');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Filtered countries for autocomplete
  const filteredCountries = countrySearch
    ? searchCountries(countrySearch)
    : countries;

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [placesData, typesData, groupedData] = await Promise.all([
        getAllPlaces(),
        getPlacesTypes({ lang, active: true }),
        getPlacesTypesGrouped(lang)
      ]);

      setPlaces(Array.isArray(placesData) ? placesData : []);
      // Handle both array response and {data: [...]} or {DATA: [...]} response (ColdFusion returns uppercase)
      const types = Array.isArray(typesData) ? typesData : (typesData?.data || typesData?.DATA || []);
      console.log('Loaded types:', types.length, types);
      setPlacesTypes(types);
      setGroupedTypes(groupedData?.categories || groupedData?.CATEGORIES || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get icon component
  const getIcon = (iconName, className = "w-5 h-5") => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className={className} /> : <Building2 className={className} />;
  };

  // Get unique categories from types
  const categories = [...new Set(placesTypes.map(t => t.category))].filter(Boolean);

  // Get types for a specific category
  const getTypesForCategory = (category) => {
    return placesTypes.filter(t => t.category === category);
  };

  // Filter places
  const filteredPlaces = places.filter(place => {
    const matchesSearch = !searchTerm ||
      place.placeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.googleAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // If no category selected, show all
    if (!selectedFilterCategory) return matchesSearch;

    // If category selected but no specific types deselected, show all from category
    const categoryTypes = getTypesForCategory(selectedFilterCategory);
    const activeTypes = categoryTypes.filter(ct => !selectedTypes.find(st => st.id === ct.id && st.excluded));

    const matchesTypes = activeTypes.some(type =>
      type.code === place.placeType || type.id === place.placeTypeId
    );

    return matchesSearch && matchesTypes;
  });

  // Select a category filter
  const handleSelectFilterCategory = (category) => {
    setSelectedFilterCategory(category);
    setSelectedTypes([]); // Reset type exclusions
  };

  // Toggle type exclusion (deselect/reselect)
  const handleToggleTypeFilter = (type) => {
    const existing = selectedTypes.find(t => t.id === type.id);
    if (existing) {
      setSelectedTypes(selectedTypes.filter(t => t.id !== type.id));
    } else {
      setSelectedTypes([...selectedTypes, { ...type, excluded: true }]);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedFilterCategory(null);
    setSelectedTypes([]);
  };

  // Exclude all types in category
  const handleExcludeAllTypes = () => {
    const categoryTypes = getTypesForCategory(selectedFilterCategory);
    setSelectedTypes(categoryTypes.map(t => ({ ...t, excluded: true })));
  };

  // Include all types in category
  const handleIncludeAllTypes = () => {
    setSelectedTypes([]);
  };

  // BCE Search by VAT number
  const handleSearchByVat = async () => {
    if (!searchVat.trim()) return;

    setBceSearching(true);
    setBceError('');
    setBceResult(null);
    setBceNameResults([]);

    try {
      const result = await searchByVatNumber(searchVat);
      if (result.success) {
        setBceResult(result.data);
      } else {
        setBceError(result.message || 'Entreprise non trouvée');
      }
    } catch (err) {
      setBceError(err.message);
    } finally {
      setBceSearching(false);
    }
  };

  // BCE Search by name
  const handleSearchByNameCBE = async () => {
    if (!searchName.trim() || searchName.length < 2) {
      setBceError('Veuillez entrer au moins 2 caractères');
      return;
    }

    setBceSearching(true);
    setBceError('');
    setBceNameResults([]);
    setBceResult(null);

    try {
      const result = await searchByName(searchName);
      if (result.success && result.data?.length > 0) {
        setBceNameResults(result.data);
      } else if (result.success && result.data?.length === 0) {
        setBceError('Aucune entreprise trouvée');
      } else {
        setBceError(result.message || 'Erreur lors de la recherche');
      }
    } catch (err) {
      setBceError(err.message);
    } finally {
      setBceSearching(false);
    }
  };

  // Select company from BCE name search results
  const handleSelectFromBce = (company) => {
    setBceResult(company);
    setBceNameResults([]);
  };

  // Apply BCE result to form
  const applyBceResult = () => {
    if (!bceResult) return;

    // Set category if detected
    if (bceResult.detectedCategory) {
      setFormCategory(bceResult.detectedCategory);
    }

    setFormData(prev => ({
      ...prev,
      placeName: bceResult.placeName || prev.placeName,
      tvaNum: bceResult.tvaNum || prev.tvaNum,
      // Don't set googleAddress - let user select via Google Maps autocomplete
      // googleAddress: bceResult.googleAddress || prev.googleAddress,
      firmType: bceResult.firmType || prev.firmType,
      phone: bceResult.phone || prev.phone,
      email: bceResult.email || prev.email,
      website: bceResult.website || prev.website
    }));
    setBceResult(null);
    setSearchVat('');
    setSearchName('');
  };

  // Autocomplete BCE - search automatically after 3 characters (name) or 6 characters (VAT)
  useEffect(() => {
    if (selectedCountry !== 'BE' || !showForm) return;

    const timer = setTimeout(() => {
      if (bceMode === 'name' && searchName.length >= 3) {
        handleSearchByNameCBE();
      } else if (bceMode === 'vat' && searchVat.replace(/[\s.]/g, '').length >= 6) {
        handleSearchByVat();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchName, searchVat, bceMode, selectedCountry, showForm]);

  // Handle address selection
  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      googleAddress: addressData.formatted_address || addressData.address,
      latitude: addressData.lat,
      longitude: addressData.lng
    }));
  };

  // Google Places autocomplete search using new API
  const searchGooglePlaces = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setPlacesSuggestions([]);
      return;
    }

    if (!window.google?.maps?.places?.AutocompleteSuggestion) {
      console.warn('Google Places API not loaded');
      return;
    }

    setPlacesSearching(true);
    try {
      // Use the new AutocompleteSuggestion API (same as AddressAutocomplete)
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedPrimaryTypes: ['establishment'],
        includedRegionCodes: selectedCountry ? [selectedCountry.toLowerCase()] : ['be', 'fr', 'nl', 'de', 'lu']
      });

      if (suggestions && suggestions.length > 0) {
        // Convert to format compatible with our UI
        const formattedSuggestions = suggestions.slice(0, 8).map(suggestion => ({
          place_id: suggestion.placePrediction?.placeId || Math.random().toString(),
          description: suggestion.placePrediction?.text?.text || '',
          main_text: suggestion.placePrediction?.mainText?.text || '',
          secondary_text: suggestion.placePrediction?.secondaryText?.text || ''
        }));

        setPlacesSuggestions(formattedSuggestions);
        setShowPlacesSuggestions(true);
      } else {
        setPlacesSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setPlacesSuggestions([]);
    } finally {
      setPlacesSearching(false);
    }
  }, [selectedCountry]);

  // Select a place from Google suggestions using new Place API
  const handleSelectGooglePlace = useCallback(async (placeId) => {
    if (!window.google?.maps?.places?.Place) return;

    try {
      const place = new window.google.maps.places.Place({ id: placeId });

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'internationalPhoneNumber', 'nationalPhoneNumber', 'websiteURI', 'types', 'addressComponents', 'photos', 'googleMapsURI', 'rating', 'userRatingCount', 'regularOpeningHours', 'businessStatus', 'priceLevel']
      });

      // Extended mapping: Google type -> { category, typeCode }
      // typeCode corresponds to Cabaza place types codes
      const googleTypeToCabaza = {
        // Food & Dining
        restaurant: { category: 'food', typeCode: 'restaurant' },
        cafe: { category: 'food', typeCode: 'cafe' },
        bakery: { category: 'food', typeCode: 'bakery' },
        bar: { category: 'food', typeCode: 'bar' },
        meal_takeaway: { category: 'food', typeCode: 'fastfood' },
        meal_delivery: { category: 'food', typeCode: 'fastfood' },
        food: { category: 'food', typeCode: 'restaurant' },
        // Shopping
        store: { category: 'shopping', typeCode: 'store' },
        shopping_mall: { category: 'shopping', typeCode: 'mall' },
        clothing_store: { category: 'shopping', typeCode: 'clothing' },
        supermarket: { category: 'shopping', typeCode: 'supermarket' },
        grocery_or_supermarket: { category: 'shopping', typeCode: 'supermarket' },
        convenience_store: { category: 'shopping', typeCode: 'convenience' },
        electronics_store: { category: 'shopping', typeCode: 'electronics' },
        furniture_store: { category: 'shopping', typeCode: 'furniture' },
        home_goods_store: { category: 'shopping', typeCode: 'home' },
        jewelry_store: { category: 'shopping', typeCode: 'jewelry' },
        book_store: { category: 'shopping', typeCode: 'books' },
        florist: { category: 'shopping', typeCode: 'florist' },
        pet_store: { category: 'shopping', typeCode: 'pets' },
        // Health
        hospital: { category: 'health', typeCode: 'hospital' },
        doctor: { category: 'health', typeCode: 'doctor' },
        pharmacy: { category: 'health', typeCode: 'pharmacy' },
        dentist: { category: 'health', typeCode: 'dentist' },
        physiotherapist: { category: 'health', typeCode: 'physio' },
        veterinary_care: { category: 'health', typeCode: 'vet' },
        beauty_salon: { category: 'health', typeCode: 'beauty' },
        hair_care: { category: 'health', typeCode: 'hairdresser' },
        spa: { category: 'health', typeCode: 'spa' },
        // Sports & Leisure
        gym: { category: 'sports', typeCode: 'gym' },
        stadium: { category: 'sports', typeCode: 'stadium' },
        bowling_alley: { category: 'sports', typeCode: 'bowling' },
        // Entertainment
        movie_theater: { category: 'entertainment', typeCode: 'cinema' },
        night_club: { category: 'entertainment', typeCode: 'nightclub' },
        casino: { category: 'entertainment', typeCode: 'casino' },
        amusement_park: { category: 'entertainment', typeCode: 'amusement' },
        museum: { category: 'entertainment', typeCode: 'museum' },
        art_gallery: { category: 'entertainment', typeCode: 'gallery' },
        // Hospitality
        lodging: { category: 'hospitality', typeCode: 'hotel' },
        hotel: { category: 'hospitality', typeCode: 'hotel' },
        campground: { category: 'hospitality', typeCode: 'camping' },
        // Education
        school: { category: 'education', typeCode: 'school' },
        university: { category: 'education', typeCode: 'university' },
        library: { category: 'education', typeCode: 'library' },
        // Services
        bank: { category: 'services', typeCode: 'bank' },
        accounting: { category: 'services', typeCode: 'accounting' },
        lawyer: { category: 'services', typeCode: 'lawyer' },
        insurance_agency: { category: 'services', typeCode: 'insurance' },
        real_estate_agency: { category: 'services', typeCode: 'realestate' },
        travel_agency: { category: 'services', typeCode: 'travel' },
        car_repair: { category: 'services', typeCode: 'garage' },
        car_dealer: { category: 'services', typeCode: 'cardealer' },
        gas_station: { category: 'services', typeCode: 'gasstation' },
        laundry: { category: 'services', typeCode: 'laundry' },
        // Transport
        airport: { category: 'transport', typeCode: 'airport' },
        train_station: { category: 'transport', typeCode: 'trainstation' },
        bus_station: { category: 'transport', typeCode: 'busstation' },
        taxi_stand: { category: 'transport', typeCode: 'taxi' },
        parking: { category: 'transport', typeCode: 'parking' },
        // Government
        local_government_office: { category: 'government', typeCode: 'government' },
        city_hall: { category: 'government', typeCode: 'cityhall' },
        courthouse: { category: 'government', typeCode: 'courthouse' },
        police: { category: 'government', typeCode: 'police' },
        fire_station: { category: 'government', typeCode: 'firestation' },
        post_office: { category: 'government', typeCode: 'postoffice' },
        // Social
        church: { category: 'social', typeCode: 'church' },
        mosque: { category: 'social', typeCode: 'mosque' },
        synagogue: { category: 'social', typeCode: 'synagogue' },
        // Business
        point_of_interest: { category: 'business', typeCode: 'enterprise' },
        establishment: { category: 'business', typeCode: 'enterprise' }
      };

      let detectedCategory = 'business';
      let detectedTypeCode = null;
      let googleTypes = [];

      if (place.types) {
        googleTypes = [...place.types];
        for (const type of place.types) {
          if (googleTypeToCabaza[type]) {
            detectedCategory = googleTypeToCabaza[type].category;
            detectedTypeCode = googleTypeToCabaza[type].typeCode;
            break;
          }
        }
      }

      // Set form category
      setFormCategory(detectedCategory);

      // Try to find and select the matching type in Cabaza
      if (detectedTypeCode) {
        const matchingType = placesTypes.find(t =>
          t.code === detectedTypeCode && t.category === detectedCategory
        );
        if (matchingType) {
          setFormData(prev => ({
            ...prev,
            placeTypeId: matchingType.id,
            placeType: matchingType.code
          }));
        }
      }

      // Get Google Maps URL
      const googleUrl = place.googleMapsURI || `https://www.google.com/maps/place/?q=place_id:${placeId}`;

      // Get photos URLs (max 6)
      const photos = [];
      if (place.photos && place.photos.length > 0) {
        for (let i = 0; i < Math.min(6, place.photos.length); i++) {
          const photo = place.photos[i];
          if (photo.getURI) {
            photos.push(photo.getURI({ maxWidth: 400, maxHeight: 300 }));
          }
        }
      }
      setGooglePlacePhotos(photos);

      // Parse opening hours
      let openingHours = null;
      if (place.regularOpeningHours?.weekdayDescriptions) {
        openingHours = place.regularOpeningHours.weekdayDescriptions;
      }

      // Extract address components
      let city = '', postalCode = '', country = '';
      if (place.addressComponents) {
        for (const component of place.addressComponents) {
          if (component.types.includes('locality')) {
            city = component.longText || component.shortText;
          }
          if (component.types.includes('postal_code')) {
            postalCode = component.longText || component.shortText;
          }
          if (component.types.includes('country')) {
            country = component.shortText; // Country code like 'BE', 'FR'
          }
        }
      }

      // Store the selected place result for display (all info for user to copy)
      setGooglePlaceResult({
        name: place.displayName,
        address: place.formattedAddress,
        phone: place.nationalPhoneNumber || place.internationalPhoneNumber,
        internationalPhone: place.internationalPhoneNumber,
        website: place.websiteURI,
        googleUrl: googleUrl,
        rating: place.rating,
        ratingCount: place.userRatingCount,
        category: detectedCategory,
        detectedTypeCode: detectedTypeCode,
        googleTypes: googleTypes,
        photos: photos,
        openingHours: openingHours,
        businessStatus: place.businessStatus,
        priceLevel: place.priceLevel,
        city: city,
        postalCode: postalCode,
        country: country,
        latitude: place.location?.lat(),
        longitude: place.location?.lng()
      });

      // Reset copied fields tracker - mark all auto-copied fields
      setCopiedFields({ placeName: true, googleAddress: true, googleUrl: true, coordinates: true, city: true });

      // Auto-fill name, address, coordinates and googleUrl
      setFormData(prev => ({
        ...prev,
        placeName: place.displayName || prev.placeName,
        googleAddress: place.formattedAddress || prev.googleAddress,
        latitude: place.location?.lat() || prev.latitude,
        longitude: place.location?.lng() || prev.longitude,
        googleUrl: googleUrl
      }));

      // Update country if detected
      if (country) {
        setSelectedCountry(country);
      }

      // Clear suggestions
      setPlacesSuggestions([]);
      setShowPlacesSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  }, [placesTypes]);

  // Debounced Google Places search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.placeName && showPlacesSuggestions) {
        searchGooglePlaces(formData.placeName);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.placeName, searchGooglePlaces, showPlacesSuggestions]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle type selection
  const handleTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      placeTypeId: type.id,
      placeType: type.code
    }));
  };

  // Open create form
  const handleCreate = () => {
    setEditingPlace(null);
    setFormCategory(''); // Reset form category
    setGooglePlaceResult(null); // Reset Google Places result
    setGooglePlacePhotos([]); // Reset photos
    setShowGoogleUrlAlert(false); // Reset alert
    setFormData({
      placeName: '',
      subName: '',
      placeTypeId: '',
      placeType: 'enterprise',
      googleAddress: '',
      latitude: null,
      longitude: null,
      email: '',
      phone: '',
      website: '',
      contactName: '',
      description: '',
      tvaNum: '',
      bankNum: '',
      firmType: '',
      facebookUrl: '',
      twitterUrl: '',
      youtubeUrl: '',
      googleUrl: '',
      parking: false,
      status: true,
      relationshipType: '',
      addToFavorites: false
    });
    setShowForm(true);
  };

  // Open edit form
  const handleEdit = async (place) => {
    setEditingPlace(place);
    setGooglePlaceResult(null); // Reset Google Places result
    setGooglePlacePhotos([]); // Reset photos
    setShowGoogleUrlAlert(false); // Reset alert
    // Find the category of the current type
    const currentType = placesTypes.find(t => t.code === place.placeType || t.id === place.placeTypeId);
    setFormCategory(currentType?.category || '');

    // Fetch user's relation with this place
    let relationshipType = '';
    let addToFavorites = false;
    if (place.id && user?.id) {
      const relationResult = await getPlaceRelation(place.id, user.id);
      console.log('handleEdit - relationResult:', relationResult);
      if (relationResult.success && relationResult.data) {
        relationshipType = relationResult.data.role || '';
        addToFavorites = relationResult.data.isfavorite || relationResult.data.isFavorite || false;
        console.log('handleEdit - loaded relation:', { relationshipType, addToFavorites });
      }
    }

    setFormData({
      placeName: place.placeName || '',
      subName: place.subName || '',
      placeTypeId: place.placeTypeId || '',
      placeType: place.placeType || 'enterprise',
      googleAddress: place.googleAddress || '',
      latitude: place.latitude,
      longitude: place.longitude,
      email: place.email || '',
      phone: place.phone || '',
      website: place.website || '',
      contactName: place.contactName || '',
      description: place.description || '',
      tvaNum: place.tvaNum || '',
      bankNum: place.bankNum || '',
      firmType: place.firmType || '',
      facebookUrl: place.facebookUrl || '',
      twitterUrl: place.twitterUrl || '',
      youtubeUrl: place.youtubeUrl || '',
      googleUrl: place.googleUrl || '',
      parking: place.parking || false,
      status: place.status !== false,
      relationshipType: relationshipType,
      addToFavorites: addToFavorites
    });
    setShowForm(true);
  };

  // Open edit form with alert for missing Google URL
  const handleEditWithAlert = async (place) => {
    await handleEdit(place);
    setShowGoogleUrlAlert(true);

    // Try to auto-fetch Google URL if we have address and Google Maps is loaded
    if (place.googleAddress && window.google?.maps?.places?.AutocompleteSuggestion) {
      try {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: `${place.placeName} ${place.googleAddress}`,
          includedPrimaryTypes: ['establishment'],
          includedRegionCodes: ['be', 'fr', 'nl', 'de', 'lu']
        });

        if (suggestions && suggestions.length > 0) {
          const placeId = suggestions[0].placePrediction?.placeId;
          if (placeId) {
            const placeObj = new window.google.maps.places.Place({ id: placeId });
            await placeObj.fetchFields({ fields: ['googleMapsURI'] });

            if (placeObj.googleMapsURI) {
              setFormData(prev => ({ ...prev, googleUrl: placeObj.googleMapsURI }));
              // Auto-save the googleUrl
              await updatePlace(place.id, { googleUrl: placeObj.googleMapsURI });
              console.log('Google URL auto-fetched and saved:', placeObj.googleMapsURI);
            }
          }
        }
      } catch (error) {
        console.error('Error auto-fetching Google URL:', error);
      }
    }
  };

  // Save place
  const handleSave = async () => {
    if (!formData.placeName) {
      alert(t.endroits?.nameRequired || 'Le nom est requis');
      return;
    }

    setSaving(true);
    try {
      let result;
      if (editingPlace) {
        result = await updatePlace(editingPlace.id, formData);
      } else {
        // Add createdById to the formData
        const dataToSave = { ...formData, createdById: user?.id };
        result = await createPlace(dataToSave);
      }

      if (result.success) {
        const placeId = editingPlace ? editingPlace.id : result.data?.id;

        // Always link the user to the place if relationshipType or addToFavorites is set
        if (placeId && user?.id) {
          const shouldLink = formData.relationshipType || formData.addToFavorites;
          if (shouldLink) {
            await linkPlaceToPeople(
              user.id,
              placeId,
              formData.relationshipType || 'visitor',
              formData.addToFavorites
            );
          }
        }
        setShowForm(false);
        loadData();
      } else {
        alert(result.message || t.common?.error || 'Erreur');
      }
    } catch (error) {
      console.error('Error saving place:', error);
      alert(t.common?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Delete place
  const handleDelete = async (place) => {
    setDeleteConfirm(place);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const result = await deletePlace(deleteConfirm.id);
      if (result.success) {
        loadData();
      } else {
        alert(result.message || t.common?.error || 'Erreur');
      }
    } catch (error) {
      console.error('Error deleting place:', error);
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Get type info for a place
  const getTypeInfo = (place) => {
    const type = placesTypes.find(t => t.code === place.placeType || t.id === place.placeTypeId);
    return type || { icon: 'Building2', color: '#6366f1', name: place.placeType || 'Entreprise' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MapPin className="w-7 h-7 text-purple-500" />
            {t.endroits?.title || 'Endroits'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.endroits?.subtitle || 'Gérez vos lieux : entreprises, clubs, restaurants...'}
          </p>
        </div>
        <Button
          onClick={handleCreate}
          icon={Plus}
        >
          {t.endroits?.add || 'Ajouter un endroit'}
        </Button>
      </div>

      {/* Filters */}
      <div className="space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t.common?.search || 'Rechercher...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          {/* Category filter dropdown */}
          <CustomDropdown
            value={selectedFilterCategory || ''}
            onChange={(val) => handleSelectFilterCategory(val || null)}
            options={[
              { value: '', label: t.endroits?.categories?.all || 'Toutes les catégories' },
              ...categories.map(cat => ({
                value: cat,
                label: t.endroits?.categories?.[cat] || cat
              }))
            ]}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-purple-400 focus:ring-2 focus:ring-purple-500 min-w-[200px]"
          />
        </div>

        {/* Selected category tag + types */}
        {selectedFilterCategory && (
          <div className="space-y-3">
            {/* Category tag with actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
              >
                {t.endroits?.categories?.[selectedFilterCategory] || selectedFilterCategory}
                <button
                  onClick={handleClearFilters}
                  className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                  title={t.endroits?.clearCategory || 'Retirer la catégorie'}
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
              {/* Toggle all types buttons */}
              <button
                onClick={handleExcludeAllTypes}
                className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={t.endroits?.deselectAll || 'Tout désélectionner'}
              >
                {t.endroits?.deselectAll || 'Tout désélectionner'}
              </button>
              <button
                onClick={handleIncludeAllTypes}
                className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={t.endroits?.selectAll || 'Tout sélectionner'}
              >
                {t.endroits?.selectAll || 'Tout sélectionner'}
              </button>
            </div>

            {/* Types within category - clickable to exclude/include */}
            <div className="flex flex-wrap gap-2">
              {getTypesForCategory(selectedFilterCategory).map(type => {
                const isExcluded = selectedTypes.find(t => t.id === type.id && t.excluded);
                return (
                  <button
                    key={type.id}
                    onClick={() => handleToggleTypeFilter(type)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isExcluded
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
                        : 'text-white'
                    }`}
                    style={!isExcluded ? { backgroundColor: type.color || '#6366f1' } : {}}
                  >
                    {getIcon(type.icon, "w-4 h-4")}
                    {type.name}
                    {isExcluded && <X className="w-3.5 h-3.5 ml-1" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Places Grid */}
      {filteredPlaces.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <MapPin className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm
              ? (t.endroits?.noResults || 'Aucun résultat trouvé')
              : (t.endroits?.empty || 'Aucun endroit enregistré')}
          </p>
          <button
            onClick={handleCreate}
            className="mt-4 text-purple-500 hover:text-purple-600 font-medium"
          >
            {t.endroits?.addFirst || 'Ajouter votre premier endroit'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlaces.map(place => {
            const typeInfo = getTypeInfo(place);
            const isMyPlace = user?.id && place.createdById === user.id;
            const isFavorite = place.isFavorite || place.isfavorite;
            return (
              <div
                key={place.id}
                className={`rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
                  isMyPlace
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400'
                    : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700'
                }`}
                onClick={() => navigate(`/endroit/${place.id}`)}
              >
                {/* Card Header with Type + Favorite */}
                <div
                  className="px-4 py-3 flex items-center gap-3"
                  style={{ backgroundColor: `${typeInfo.color}15` }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: typeInfo.color }}
                  >
                    {getIcon(typeInfo.icon, "w-5 h-5 text-white")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {place.placeName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {typeInfo.name}
                    </p>
                  </div>
                  {/* Favorite heart */}
                  {isFavorite && (
                    <Heart className="w-5 h-5 text-red-500 fill-red-500 flex-shrink-0" />
                  )}
                  {/* Rating stars if available */}
                  {place.totalReviews > 0 && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {(place.totalStars / place.totalReviews).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-2">
                  {place.googleAddress && (
                    <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                      <span className="line-clamp-2">{place.googleAddress}</span>
                    </div>
                  )}
                  {place.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Phone className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span>{place.phone}</span>
                    </div>
                  )}
                  {place.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Mail className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="truncate">{place.email}</span>
                    </div>
                  )}
                  {place.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(place.website.startsWith('http') ? place.website : `https://${place.website}`, '_blank');
                        }}
                        className="text-purple-500 hover:text-purple-600 truncate flex items-center gap-1 cursor-pointer"
                      >
                        {place.website.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  )}
                  {place.tvaNum && (
                    <div className="text-xs text-gray-400 mt-2">
                      TVA: {place.tvaNum}
                    </div>
                  )}

                </div>

                {/* Card Actions + Created by */}
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    {/* Created by + Date */}
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      {place.createdById ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.dispatchEvent(new CustomEvent('openPeopleDetail', { detail: { peopleId: place.createdById } }));
                          }}
                          className="hover:text-purple-500 hover:underline"
                        >
                          {place.createdByName || `#${place.createdById}`}
                        </button>
                      ) : (
                        <span>{place.createdByName || 'Inconnu'}</span>
                      )}
                      <span>•</span>
                      <span>{place.createdAt && new Date(place.createdAt).toLocaleDateString(lang)}</span>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(place);
                        }}
                        className="p-2 text-gray-500 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        title={t.common?.edit || 'Modifier'}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(place);
                        }}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title={t.common?.delete || 'Supprimer'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingPlace ? (t.endroits?.edit || 'Modifier l\'endroit') : (t.endroits?.create || 'Nouvel endroit')}
        icon={MapPin}
        size="lg"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              {t.common?.cancel || 'Annuler'}
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              icon={Save}
            >
              {t.common?.save || 'Enregistrer'}
            </Button>
          </>
        }
      >
              <div className="space-y-6">
                {/* Country Selection with Autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-500" />
                      {t.endroits?.country || 'Pays'}
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={countrySearch || getCountryByCode(selectedCountry)?.name || ''}
                      onChange={(e) => {
                        setCountrySearch(e.target.value);
                        setShowCountryDropdown(true);
                      }}
                      onFocus={() => setShowCountryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                      placeholder="Rechercher un pays..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg">
                      {getCountryByCode(selectedCountry)?.flag || '🌍'}
                    </span>
                  </div>
                  {showCountryDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg max-h-60 overflow-y-auto">
                      {filteredCountries.slice(0, 50).map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(country.code);
                            setCountrySearch('');
                            setShowCountryDropdown(false);
                            setBceResult(null);
                            setBceNameResults([]);
                            setBceError('');
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-3 ${
                            selectedCountry === country.code ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                          }`}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span className="flex-1 text-gray-900 dark:text-white">{country.name}</span>
                          <span className="text-xs text-gray-500">{country.phonePrefix}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* BCE Search for Belgium */}
                {selectedCountry === 'BE' && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 space-y-4">
                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                      <Building2 className="w-5 h-5" />
                      <span className="font-medium">Recherche BCE (Banque Carrefour des Entreprises)</span>
                    </div>

                    {/* Mode tabs: by VAT or by Name */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setBceMode('vat');
                          setBceError('');
                          setBceNameResults([]);
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                          bceMode === 'vat'
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        Par N° TVA
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setBceMode('name');
                          setBceError('');
                          setBceResult(null);
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                          bceMode === 'name'
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        Par nom
                      </button>
                    </div>

                    {/* Search by VAT number */}
                    {bceMode === 'vat' && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchVat}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchVat(value);
                            if (!value.trim()) {
                              // Clear results when field is emptied
                              setBceResult(null);
                              setBceNameResults([]);
                            }
                            setBceResultsHidden(false);
                          }}
                          onFocus={() => setBceResultsHidden(false)} // Show results on focus
                          placeholder="Ex: 0123.456.789 ou BE0123456789"
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchByVat()}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBceResultsHidden(false); // Show results
                            if (bceResult || bceNameResults.length > 0) return; // Already have results
                            handleSearchByVat();
                          }}
                          disabled={bceSearching || !searchVat.trim()}
                          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {bceSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    {/* Search by name */}
                    {bceMode === 'name' && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchName}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchName(value);
                            if (!value.trim()) {
                              // Clear results when field is emptied
                              setBceResult(null);
                              setBceNameResults([]);
                            }
                            setBceResultsHidden(false);
                          }}
                          onFocus={() => setBceResultsHidden(false)} // Show results on focus
                          placeholder="Nom de l'entreprise..."
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchByNameCBE()}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBceResultsHidden(false); // Show results
                            if (bceResult || bceNameResults.length > 0) return; // Already have results
                            handleSearchByNameCBE();
                          }}
                          disabled={bceSearching || !searchName.trim()}
                          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {bceSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    {/* Error message */}
                    {bceError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {bceError}
                      </div>
                    )}

                    {/* Name search results list */}
                    {bceNameResults.length > 0 && !bceResultsHidden && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {/* Header with close button */}
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {bceNameResults.length} résultat{bceNameResults.length > 1 ? 's' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => setBceResultsHidden(true)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            title="Fermer les résultats"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                        {/* Results list */}
                        <div className="max-h-60 overflow-y-auto">
                          {bceNameResults.map((result, index) => {
                            const ResultIcon = result.detectedIcon ? LucideIcons[result.detectedIcon] : Building2;
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleSelectFromBce(result)}
                                className="w-full p-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                                    {ResultIcon && <ResultIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white truncate">{result.placeName}</p>
                                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                      <span>{result.tvaNum}</span>
                                      {result.detectedCategory && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                          {t.endroits?.categories?.[result.detectedCategory] || result.detectedCategory}
                                        </span>
                                      )}
                                    </div>
                                    {result.city && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{result.zip} {result.city}</p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Selected BCE result */}
                    {bceResult && !bceResultsHidden && (() => {
                      const SelectedIcon = bceResult.detectedIcon ? LucideIcons[bceResult.detectedIcon] : Building2;
                      // Find matching type for color
                      const matchingType = placesTypes.find(t =>
                        t.category === bceResult.detectedCategory || t.code === bceResult.detectedType
                      );
                      const typeColor = matchingType?.color || '#9333ea'; // Purple fallback
                      return (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-500 overflow-hidden">
                          {/* Close button */}
                          <div className="flex justify-end px-2 py-1">
                            <button
                              type="button"
                              onClick={() => setBceResultsHidden(true)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Fermer"
                            >
                              <X className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                          <div className="px-4 pb-4">
                            <div className="flex items-start gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                style={{ backgroundColor: typeColor }}
                              >
                                {SelectedIcon && <SelectedIcon className="w-5 h-5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 dark:text-white">{bceResult.placeName}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-sm text-purple-600 dark:text-purple-400">{bceResult.tvaNum}</p>
                                  {bceResult.detectedCategory && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                      {t.endroits?.categories?.[bceResult.detectedCategory] || bceResult.detectedCategory}
                                    </span>
                                  )}
                                </div>
                                {bceResult.city && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{bceResult.zip} {bceResult.city}</p>
                                )}
                                {bceResult.firmType && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{bceResult.firmType}</p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={applyBceResult}
                              className="mt-3 w-full py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              Utiliser ces données
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Les données seront récupérées automatiquement via la Banque Carrefour des Entreprises
                    </p>
                  </div>
                )}

                {/* Category & Type Selection */}
                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        {t.endroits?.category || 'Catégorie'} *
                      </span>
                    </label>
                    <CustomDropdown
                      value={formCategory}
                      onChange={(val) => {
                        setFormCategory(val);
                        // Reset type selection when category changes
                        setFormData(prev => ({ ...prev, placeTypeId: '', placeType: '' }));
                      }}
                      options={[
                        { value: '', label: t.endroits?.selectCategory || '-- Sélectionner une catégorie --' },
                        ...categories.map(cat => ({
                          value: cat,
                          label: t.endroits?.categories?.[cat] || cat
                        }))
                      ]}
                      className="w-full px-3 py-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-purple-600 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Type Selection - only show when category is selected */}
                  {formCategory && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <span className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-purple-500" />
                          {t.endroits?.type || 'Type d\'endroit'} *
                        </span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg">
                        {getTypesForCategory(formCategory).map(type => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => handleTypeSelect(type)}
                            className={`flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                              formData.placeTypeId === type.id || formData.placeType === type.code
                                ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                                : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-2 border-transparent'
                            }`}
                          >
                            <span
                              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: type.color }}
                            >
                              {getIcon(type.icon, "w-4 h-4 text-white")}
                            </span>
                            <span className="truncate text-gray-700 dark:text-gray-300">{type.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        {t.endroits?.name || 'Nom'} *
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="placeName"
                        value={formData.placeName}
                        onChange={(e) => {
                          handleInputChange(e);
                          setShowPlacesSuggestions(true);
                        }}
                        onFocus={() => setShowPlacesSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowPlacesSuggestions(false), 200)}
                        className="w-full px-3 py-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white pr-10 focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                        placeholder={t.endroits?.namePlaceholder || 'Rechercher un établissement...'}
                        required
                      />
                      {placesSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                      )}
                    </div>

                    {/* Google Places suggestions dropdown */}
                    {showPlacesSuggestions && placesSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg max-h-60 overflow-y-auto">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Search className="w-3 h-3" />
                            Google Places
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPlacesSuggestions(false)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                          >
                            <X className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                        {placesSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.place_id}
                            type="button"
                            onClick={() => handleSelectGooglePlace(suggestion.place_id)}
                            className="w-full p-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                                <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                  {suggestion.main_text || suggestion.description}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {suggestion.secondary_text || ''}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-500" />
                        {t.endroits?.subName || 'Sous-titre / Slogan'}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="subName"
                      value={formData.subName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Google Places Result Card with Photos and Copy Buttons */}
                {googlePlaceResult && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-blue-500 overflow-hidden">
                    {/* Photos carousel */}
                    {googlePlacePhotos.length > 0 && (
                      <div className="flex overflow-x-auto gap-1 bg-gray-100 dark:bg-gray-900">
                        {googlePlacePhotos.map((photoUrl, index) => (
                          <img
                            key={index}
                            src={photoUrl}
                            alt={`${googlePlaceResult.name} - ${index + 1}`}
                            className="h-32 w-auto object-cover flex-shrink-0"
                          />
                        ))}
                      </div>
                    )}
                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-lg">{googlePlaceResult.name}</h4>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {googlePlaceResult.rating && (
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {googlePlaceResult.rating} ({googlePlaceResult.ratingCount} avis)
                                </span>
                              </div>
                            )}
                            {googlePlaceResult.priceLevel && (
                              <span className="text-sm text-gray-500">
                                {'€'.repeat(googlePlaceResult.priceLevel)}
                              </span>
                            )}
                            {googlePlaceResult.businessStatus && googlePlaceResult.businessStatus !== 'OPERATIONAL' && (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-600">
                                {googlePlaceResult.businessStatus === 'CLOSED_TEMPORARILY' ? 'Fermé temporairement' : 'Fermé'}
                              </span>
                            )}
                          </div>
                          {/* Google Types */}
                          {googlePlaceResult.googleTypes && googlePlaceResult.googleTypes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {googlePlaceResult.googleTypes.slice(0, 5).map((type, idx) => (
                                <span key={idx} className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  {type.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                          {googlePlaceResult.category && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">Catégorie Cabaza détectée: </span>
                              <span className="inline-block text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                {t.endroits?.categories?.[googlePlaceResult.category] || googlePlaceResult.category}
                              </span>
                              {googlePlaceResult.detectedTypeCode && (
                                <span className="ml-1 text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                  {googlePlaceResult.detectedTypeCode}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setGooglePlaceResult(null);
                            setGooglePlacePhotos([]);
                            setCopiedFields({});
                          }}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>

                      {/* Copyable info section */}
                      <div className="mt-4 space-y-2 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Cliquez sur un élément pour le copier dans le formulaire
                        </p>

                        {/* Name - auto-copied */}
                        <div className={`flex items-center gap-2 p-2 rounded transition-colors ${
                          copiedFields.placeName
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                        }`}
                          onClick={() => {
                            if (!copiedFields.placeName) {
                              setFormData(prev => ({ ...prev, placeName: googlePlaceResult.name }));
                              setCopiedFields(prev => ({ ...prev, placeName: true }));
                            }
                          }}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${copiedFields.placeName ? 'text-green-500' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-500 w-16">Nom</span>
                          <span className={`flex-1 text-sm truncate ${copiedFields.placeName ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                            {googlePlaceResult.name}
                          </span>
                          {copiedFields.placeName ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-300" />
                          )}
                        </div>

                        {/* Address - auto-copied */}
                        {googlePlaceResult.address && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.googleAddress
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                          }`}>
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${copiedFields.googleAddress ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Adresse</span>
                            <span className={`flex-1 text-sm truncate ${copiedFields.googleAddress ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.address}
                            </span>
                            {copiedFields.googleAddress ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-300" />
                            )}
                          </div>
                        )}

                        {/* City & Postal Code */}
                        {(googlePlaceResult.city || googlePlaceResult.postalCode) && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.city ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${copiedFields.city ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Ville</span>
                            <span className={`flex-1 text-sm ${copiedFields.city ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.postalCode} {googlePlaceResult.city} {googlePlaceResult.country && `(${googlePlaceResult.country})`}
                            </span>
                            {copiedFields.city && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                        {/* Phone */}
                        {googlePlaceResult.phone && (
                          <div
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                              copiedFields.phone
                                ? 'bg-green-50 dark:bg-green-900/20'
                                : 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            }`}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, phone: googlePlaceResult.phone }));
                              setCopiedFields(prev => ({ ...prev, phone: true }));
                            }}
                          >
                            <Phone className={`w-4 h-4 flex-shrink-0 ${copiedFields.phone ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Tél</span>
                            <span className={`flex-1 text-sm ${copiedFields.phone ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.phone}
                              {googlePlaceResult.internationalPhone && googlePlaceResult.internationalPhone !== googlePlaceResult.phone && (
                                <span className="ml-2 text-xs text-gray-400">({googlePlaceResult.internationalPhone})</span>
                              )}
                            </span>
                            {copiedFields.phone ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-300" />
                            )}
                          </div>
                        )}

                        {/* Website */}
                        {googlePlaceResult.website && (
                          <div
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                              copiedFields.website
                                ? 'bg-green-50 dark:bg-green-900/20'
                                : 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            }`}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, website: googlePlaceResult.website }));
                              setCopiedFields(prev => ({ ...prev, website: true }));
                            }}
                          >
                            <Globe className={`w-4 h-4 flex-shrink-0 ${copiedFields.website ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Site web</span>
                            <span className={`flex-1 text-sm truncate ${copiedFields.website ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.website}
                            </span>
                            {copiedFields.website ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-300" />
                            )}
                          </div>
                        )}

                        {/* Google URL - auto-copied */}
                        {googlePlaceResult.googleUrl && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.googleUrl
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <ExternalLink className={`w-4 h-4 flex-shrink-0 ${copiedFields.googleUrl ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Google</span>
                            <span className={`flex-1 text-sm truncate ${copiedFields.googleUrl ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.googleUrl}
                            </span>
                            {copiedFields.googleUrl && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                        {/* Coordinates */}
                        {googlePlaceResult.latitude && googlePlaceResult.longitude && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.coordinates ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${copiedFields.coordinates ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">GPS</span>
                            <span className={`flex-1 text-sm ${copiedFields.coordinates ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.latitude.toFixed(6)}, {googlePlaceResult.longitude.toFixed(6)}
                            </span>
                            {copiedFields.coordinates && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                        {/* Opening Hours */}
                        {googlePlaceResult.openingHours && googlePlaceResult.openingHours.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Horaires d'ouverture</span>
                            </div>
                            <div className="space-y-1 pl-6">
                              {googlePlaceResult.openingHours.map((hour, idx) => (
                                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400">{hour}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => window.open(googlePlaceResult.googleUrl, '_blank', 'width=800,height=600')}
                          className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Voir sur Google Maps
                        </button>
                      </div>
                      {/* Powered by Google */}
                      <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-xs text-gray-400">Powered by</span>
                        <svg className="h-4" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                          <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                          <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
                          <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
                          <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
                          <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {/* Address */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t.endroits?.address || 'Adresse'}
                    </label>
                    <AddressAutocomplete
                      value={formData.googleAddress}
                      onChange={(field, value) => setFormData(prev => ({ ...prev, googleAddress: value }))}
                      onCoordinatesChange={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))}
                      label={t.endroits?.addressPlaceholder || 'Rechercher une adresse...'}
                    />
                  </div>

                  {/* Map and coordinates */}
                  {formData.latitude && formData.longitude && (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      {/* Google Maps iframe */}
                      <div className="h-48 w-full">
                        <iframe
                          title="Location map"
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${formData.latitude},${formData.longitude}&zoom=16`}
                          allowFullScreen
                        />
                      </div>
                      {/* Coordinates display */}
                      <div className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <MapPin className="w-4 h-4 text-purple-500" />
                          <span>Lat: {formData.latitude.toFixed(6)}</span>
                          <span className="text-gray-300 dark:text-gray-600">|</span>
                          <span>Lng: {formData.longitude.toFixed(6)}</span>
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-purple-500 hover:text-purple-600 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {t.endroits?.openInMaps || 'Ouvrir dans Maps'}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Email */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-purple-500" />
                        {t.common?.email || 'Email'}
                      </span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Clear error on change
                        if (formErrors.email) {
                          setFormErrors(prev => ({ ...prev, email: null }));
                        }
                      }}
                      onBlur={() => {
                        if (formData.email && !isValidEmail(formData.email)) {
                          setFormErrors(prev => ({ ...prev, email: 'Adresse email invalide' }));
                        }
                      }}
                      placeholder="contact@example.com"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        formErrors.email ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    <div className="absolute left-3 top-[2.1rem] text-gray-400">
                      <Mail className="w-4 h-4" />
                    </div>
                    {formErrors.email && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone with country prefix */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-purple-500" />
                        {t.common?.phone || 'Téléphone'}
                      </span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm">
                        {getPhonePrefix(selectedCountry)}
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (formErrors.phone) {
                            setFormErrors(prev => ({ ...prev, phone: null }));
                          }
                        }}
                        onBlur={() => {
                          if (formData.phone && !isValidPhone(formData.phone)) {
                            setFormErrors(prev => ({ ...prev, phone: 'Numéro de téléphone invalide' }));
                          }
                        }}
                        placeholder="470 12 34 56"
                        className={`flex-1 px-3 py-2 border rounded-r-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                          formErrors.phone ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                    </div>
                    {formErrors.phone && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* Website with protocol selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-purple-500" />
                        {t.endroits?.website || 'Site web'}
                      </span>
                    </label>
                    <div className="flex">
                      <CustomDropdown
                        value={websiteProtocol}
                        onChange={(val) => setWebsiteProtocol(val)}
                        options={[
                          { value: 'https://', label: 'https://' },
                          { value: 'http://', label: 'http://' }
                        ]}
                        className="px-2 py-2 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm"
                      />
                      <input
                        type="text"
                        name="website"
                        value={formData.website?.replace(/^https?:\/\//, '') || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prepend protocol only if user starts typing
                          const fullUrl = value ? `${websiteProtocol}${value}` : '';
                          setFormData(prev => ({ ...prev, website: fullUrl }));
                          if (formErrors.website) {
                            setFormErrors(prev => ({ ...prev, website: null }));
                          }
                        }}
                        onBlur={() => {
                          if (formData.website && !isValidUrl(formData.website)) {
                            setFormErrors(prev => ({ ...prev, website: 'URL invalide' }));
                          }
                        }}
                        placeholder="www.example.com"
                        className={`flex-1 px-3 py-2 border rounded-r-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                          formErrors.website ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                    </div>
                    {formErrors.website && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.website}
                      </p>
                    )}
                  </div>

                  {/* Contact person */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-500" />
                        {t.endroits?.contactName || 'Personne de contact'}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      placeholder="Jean Dupont"
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Business Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* TVA Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-purple-500" />
                        {t.endroits?.tvaNum || 'Numéro TVA'}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="tvaNum"
                      value={formData.tvaNum}
                      onChange={handleInputChange}
                      placeholder={selectedCountry === 'BE' ? 'Ex: BE 0123.456.789' : 'Ex: FR12345678901'}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      readOnly={selectedCountry === 'BE' && formData.tvaNum}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-purple-500" />
                        {t.endroits?.firmType || 'Forme juridique'}
                      </span>
                    </label>
                    <CustomDropdown
                      value={formData.firmType}
                      onChange={(val) => setFormData(prev => ({ ...prev, firmType: val }))}
                      options={[
                        { value: '', label: '-- Sélectionner --' },
                        { value: 'SA', label: 'SA - Société Anonyme' },
                        { value: 'SRL', label: 'SRL - Société à Responsabilité Limitée' },
                        { value: 'SPRL', label: 'SPRL - Société Privée à Responsabilité Limitée' },
                        { value: 'SC', label: 'SC - Société Coopérative' },
                        { value: 'SNC', label: 'SNC - Société en Nom Collectif' },
                        { value: 'ASBL', label: 'ASBL - Association Sans But Lucratif' },
                        { value: 'Indépendant', label: 'Indépendant' },
                        { value: 'Autre', label: 'Autre' }
                      ]}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-purple-400"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-500" />
                      {t.common?.description || 'Description'}
                    </span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                  />
                </div>

                {/* Social Media */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </span>
                    </label>
                    <input
                      type="url"
                      name="facebookUrl"
                      value={formData.facebookUrl}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (formErrors.facebookUrl) {
                          setFormErrors(prev => ({ ...prev, facebookUrl: null }));
                        }
                      }}
                      onBlur={() => {
                        if (formData.facebookUrl && !/^https?:\/\/(www\.)?facebook\.com\/.+/i.test(formData.facebookUrl)) {
                          setFormErrors(prev => ({ ...prev, facebookUrl: 'URL Facebook invalide' }));
                        }
                      }}
                      placeholder="https://facebook.com/..."
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        formErrors.facebookUrl ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    {formErrors.facebookUrl && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.facebookUrl}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Twitter className="w-4 h-4 text-sky-500" />
                        Twitter/X
                      </span>
                    </label>
                    <input
                      type="url"
                      name="twitterUrl"
                      value={formData.twitterUrl}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (formErrors.twitterUrl) {
                          setFormErrors(prev => ({ ...prev, twitterUrl: null }));
                        }
                      }}
                      onBlur={() => {
                        if (formData.twitterUrl && !/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/i.test(formData.twitterUrl)) {
                          setFormErrors(prev => ({ ...prev, twitterUrl: 'URL Twitter/X invalide' }));
                        }
                      }}
                      placeholder="https://twitter.com/..."
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        formErrors.twitterUrl ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    {formErrors.twitterUrl && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.twitterUrl}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-red-600" />
                        YouTube
                      </span>
                    </label>
                    <input
                      type="url"
                      name="youtubeUrl"
                      value={formData.youtubeUrl}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (formErrors.youtubeUrl) {
                          setFormErrors(prev => ({ ...prev, youtubeUrl: null }));
                        }
                      }}
                      onBlur={() => {
                        if (formData.youtubeUrl && !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/i.test(formData.youtubeUrl)) {
                          setFormErrors(prev => ({ ...prev, youtubeUrl: 'URL YouTube invalide' }));
                        }
                      }}
                      placeholder="https://youtube.com/..."
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        formErrors.youtubeUrl ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    {formErrors.youtubeUrl && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.youtubeUrl}
                      </p>
                    )}
                  </div>
                </div>

                {/* Google URL */}
                <div className={`rounded-xl p-4 ${showGoogleUrlAlert && !formData.googleUrl ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-500' : 'bg-gray-50 dark:bg-gray-900/50'}`}>
                  {showGoogleUrlAlert && !formData.googleUrl && (
                    <div className="flex items-center gap-2 mb-3 text-red-600 dark:text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Google URL manquante ! Utilisez l'autocomplete d'adresse ci-dessus pour la remplir automatiquement ou copiez-la manuellement depuis Google Maps.
                      </span>
                    </div>
                  )}
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <svg className="h-4 mr-2" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
                    </svg>
                    Google Maps URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="googleUrl"
                      value={formData.googleUrl || ''}
                      onChange={handleInputChange}
                      placeholder="https://maps.google.com/..."
                      className={`flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        showGoogleUrlAlert && !formData.googleUrl
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    {formData.googleUrl && (
                      <button
                        type="button"
                        onClick={() => window.open(formData.googleUrl, '_blank', 'width=800,height=600')}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Relationship Type */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t.endroits?.relationshipType || 'Ma relation avec cet établissement'}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {RELATIONSHIP_TYPES.map(rel => {
                        const RelIcon = LucideIcons[rel.icon] || User;
                        const isSelected = formData.relationshipType === rel.value;
                        return (
                          <button
                            key={rel.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, relationshipType: rel.value }))}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-all ${
                              isSelected
                                ? 'bg-purple-500 text-white shadow-md'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                            }`}
                          >
                            <RelIcon className="w-5 h-5" />
                            <span className="text-xs font-medium text-center">{rel.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add to favorites - heart toggle */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, addToFavorites: !prev.addToFavorites }))}
                    className={`flex items-center gap-3 w-full p-3 rounded-lg transition-all ${
                      formData.addToFavorites
                        ? 'bg-pink-100 dark:bg-pink-900/30 border-2 border-pink-400'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-pink-50 dark:hover:bg-pink-900/20'
                    }`}
                  >
                    <Heart className={`w-6 h-6 transition-all ${formData.addToFavorites ? 'text-pink-500 fill-pink-500 scale-110' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${formData.addToFavorites ? 'text-pink-600 dark:text-pink-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {formData.addToFavorites
                        ? (t.endroits?.inFavorites || 'Dans mes favoris')
                        : (t.endroits?.addToFavorites || 'Ajouter à mes endroits favoris')
                      }
                    </span>
                  </button>
                </div>

                {/* Options with toggle switches */}
                <div className="flex items-center gap-8">
                  {/* Parking toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="parking"
                        checked={formData.parking}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.parking ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.parking ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t.endroits?.parking || 'Parking disponible'}
                    </span>
                  </label>

                  {/* Status toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.status ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.status ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t.common?.active || 'Actif'}
                    </span>
                  </label>
                </div>
              </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={t.endroits?.deleteTitle || 'Supprimer cet endroit ?'}
        message={`${t.endroits?.deleteMessage || 'Êtes-vous sûr de vouloir supprimer'} "${deleteConfirm?.placeName}" ? ${t.common?.irreversible || 'Cette action est irréversible.'}`}
        confirmText={t.common?.delete || 'Supprimer'}
        confirmVariant="danger"
      />
    </div>
  );
};

export default EndroitsPage;
